// Axis color identity (instructor feedback, pt 5): every tone is derived from the axis hex chosen in Settings,
// so the tree, the dashboard and the PDFs share one palette per axis. Text tones are darkened until they reach
// WCAG AA (4.5:1) on both white and the tint, so light axis colors (yellow, lime) stay readable.

type Rgb = [number, number, number];

const parse = (hex: string): Rgb => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const n = parseInt(m ? m[1]! : "1F6FB2", 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (c: Rgb) => `#${c.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
/** Linear mix: weight 0 = a, 1 = b. */
const mix = (a: Rgb, b: Rgb, w: number): Rgb => [a[0] + (b[0] - a[0]) * w, a[1] + (b[1] - a[1]) * w, a[2] + (b[2] - a[2]) * w];

function luminance([r, g, b]: Rgb) {
  const ch = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

export function contrast(a: string, b: string) {
  const [x, y] = [luminance(parse(a)), luminance(parse(b))].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

export interface AxisTones {
  /** The axis color as chosen (bars, stripes, chart fills). */
  base: string;
  /** Very light background band (≈ 10% of the color). */
  tint: string;
  /** Slightly stronger background for nested headers (≈ 18%). */
  tintStrong: string;
  /** Border tone (≈ 40%). */
  border: string;
  /** Text tone readable on white and on both tints (≥ 4.5:1). */
  text: string;
  /** Text to place on top of the base color itself (white or near-black). */
  onBase: string;
}

export function axisTones(hex: string): AxisTones {
  const c = parse(hex);
  const tint = toHex(mix(WHITE, c, 0.1));
  const tintStrong = toHex(mix(WHITE, c, 0.18));
  let text = c;
  for (let i = 0; i < 20 && Math.min(contrast(toHex(text), "#FFFFFF"), contrast(toHex(text), tintStrong)) < 4.5; i++) {
    text = mix(text, BLACK, 0.12);
  }
  const base = toHex(c);
  return {
    base,
    tint,
    tintStrong,
    border: toHex(mix(WHITE, c, 0.4)),
    text: toHex(text),
    onBase: contrast(base, "#FFFFFF") >= 4.5 ? "#FFFFFF" : "#13253D",
  };
}
